import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import moment from 'moment';

import { DashboardError } from '../../components/JobsAccount';
import {
  IResumeContactRequest,
  ResumeContactRequestStatus,
  useBlockSeekerResumeContactRequestMutation,
  useGetSeekerResumeContactHistoryQuery,
  useReportSeekerResumeContactRequestMutation,
  useUnblockResumeEmployerMutation,
} from '../../services/serviceResume';

const REASON_MAX = 1000;

const statusLabels: Record<ResumeContactRequestStatus, string> = {
  0: 'Waiting for you',
  1: 'You shared your details',
  2: 'You declined',
  3: 'Expired',
  4: 'Access withdrawn',
};

const statusColors: Record<ResumeContactRequestStatus, 'default' | 'success' | 'warning'> = {
  0: 'warning',
  1: 'success',
  2: 'default',
  3: 'default',
  4: 'default',
};

const formatMoment = (value: string | null): string | null => {
  if (!value) return null;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('MMM D, YYYY') : null;
};

const ReportDialog = ({
  request,
  onClose,
}: {
  request: IResumeContactRequest;
  onClose: () => void;
}) => {
  const [reason, setReason] = useState('');
  const [report, reportResult] = useReportSeekerResumeContactRequestMutation();

  const submit = async () => {
    try {
      await report({ id: request.id, reason: reason.trim() }).unwrap();
      onClose();
    } catch {
      // The error alert stays open so the candidate can retry.
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Report {request.employerName}</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Reporting also blocks this employer and withdraws any access you approved. The BevNET team
          reviews every report and can suspend an employer&rsquo;s Resume Database access.
        </Typography>
        {reportResult.error ? (
          <Alert severity="error" sx={{ mb: 2 }}>We could not save your report. Please try again.</Alert>
        ) : null}
        <TextField
          value={reason}
          onChange={(event) => setReason(event.target.value.slice(0, REASON_MAX))}
          label="What happened?"
          required
          multiline
          minRows={3}
          fullWidth
          inputProps={{ maxLength: REASON_MAX }}
          helperText={`${REASON_MAX - reason.trim().length} characters remaining.`}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="error"
          variant="contained"
          disabled={!reason.trim() || reportResult.isLoading}
          onClick={submit}
        >
          Report and block
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ContactRow = ({
  request,
  onReport,
}: {
  request: IResumeContactRequest;
  onReport: (request: IResumeContactRequest) => void;
}) => {
  const [block, blockResult] = useBlockSeekerResumeContactRequestMutation();
  const [unblock, unblockResult] = useUnblockResumeEmployerMutation();
  const sent = formatMoment(request.createdDate);
  const responded = formatMoment(request.respondedDate);
  const busy = blockResult.isLoading || unblockResult.isLoading;

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      justifyContent="space-between"
      spacing={2}
      sx={{ py: 2.25 }}
    >
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography fontWeight={800}>{request.employerName}</Typography>
          {request.isBlocked ? (
            <Chip icon={<BlockIcon />} label="Blocked" size="small" color="error" />
          ) : null}
          {request.reportedDate ? <Chip label="Reported" size="small" variant="outlined" /> : null}
        </Stack>
        <Typography color="text.secondary" variant="body2">
          {request.listingTitle || 'No listing attached'}
          {sent ? ` · Asked ${sent}` : ''}
          {responded ? ` · You answered ${responded}` : ''}
        </Typography>
        {request.status === 4 ? (
          <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
            You withdrew this employer&rsquo;s access. Unblocking does not give it back.
          </Typography>
        ) : null}
        {request.status === 0 ? (
          <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
            Use the approve or decline link in the email this employer sent you.
          </Typography>
        ) : null}
        {blockResult.error || unblockResult.error ? (
          <DashboardError
            error={blockResult.error || unblockResult.error}
            fallback="We could not update this employer. Please try again."
          />
        ) : null}
      </Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={1}
        sx={{ flexShrink: 0 }}
      >
        <Chip label={statusLabels[request.status] || 'Unknown'} color={statusColors[request.status] || 'default'} />
        {request.isBlocked && request.blockId ? (
          <Button size="small" disabled={busy} onClick={() => unblock(request.blockId as number)}>
            Unblock
          </Button>
        ) : null}
        {!request.isBlocked ? (
          <>
            <Button size="small" disabled={busy} onClick={() => block(request.id)}>Block</Button>
            {!request.reportedDate ? (
              <Button size="small" color="error" disabled={busy} onClick={() => onReport(request)}>
                Report
              </Button>
            ) : null}
          </>
        ) : null}
      </Stack>
    </Stack>
  );
};

// One page of the descending-ID candidate feed. Each page owns its query so
// loading an older page never re-orders or duplicates the rows already shown.
const ContactHistoryPage = ({
  beforeId,
  isFirst,
  isLast,
  onLoadOlder,
  onReport,
}: {
  beforeId?: number;
  isFirst: boolean;
  isLast: boolean;
  onLoadOlder: (nextId: number) => void;
  onReport: (request: IResumeContactRequest) => void;
}) => {
  const page = useGetSeekerResumeContactHistoryQuery(beforeId ? { beforeId } : {});
  const rows = page.data?.rows || [];
  const nextId = page.data?.nextId || null;

  return (
    <>
      <DashboardError error={page.error} fallback="We could not load your employer contact history." />
      {rows.map((request) => (
        <React.Fragment key={request.id}>
          <ContactRow request={request} onReport={onReport} />
          <Divider flexItem />
        </React.Fragment>
      ))}
      {isFirst && page.data && !rows.length ? (
        <Typography color="text.secondary" sx={{ py: 2 }}>
          No employer has asked to connect with you yet.
        </Typography>
      ) : null}
      {page.isFetching ? (
        <Stack alignItems="center" sx={{ py: 2 }}><CircularProgress size={22} /></Stack>
      ) : null}
      {isLast && nextId ? (
        <Box sx={{ pt: 2, textAlign: 'center' }}>
          <Button variant="outlined" onClick={() => onLoadOlder(nextId)}>Load older</Button>
        </Box>
      ) : null}
    </>
  );
};

export const EmployerContactHistory = () => {
  const [cursors, setCursors] = useState<Array<number | undefined>>([undefined]);
  const [reporting, setReporting] = useState<IResumeContactRequest | null>(null);

  return (
    <>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Every employer who has asked to connect with you, and what you decided. Blocking stops an
        employer contacting you again and withdraws access you already approved.
        <strong> Unblocking does not restore that access</strong>. They would have to request a new
        introduction, and you would decide again.
      </Typography>
      {cursors.map((cursor, index) => (
        <ContactHistoryPage
          key={cursor === undefined ? 'newest' : cursor}
          beforeId={cursor}
          isFirst={index === 0}
          isLast={index === cursors.length - 1}
          onLoadOlder={(nextId) => setCursors((current) => (
            current.indexOf(nextId) >= 0 ? current : current.concat([nextId])
          ))}
          onReport={setReporting}
        />
      ))}
      {reporting ? (
        <ReportDialog request={reporting} onClose={() => setReporting(null)} />
      ) : null}
    </>
  );
};

export default EmployerContactHistory;
